import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ListFilter, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { ConfirmToggleDialog } from '@/components/shared/confirm-toggle-dialog';
import {
	usePublishedCSCategories,
	useEnabledIntentFilterConfigs,
	useUpsertIntentFilterConfig,
	useBulkIntentFilterConfig,
	useAccountFlags,
	useUpdateIntentFilteringEnabled,
	useBypassTaggingConfigs,
	INTENT_FILTERING_ENABLED_FLAG
} from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import {
	SETTINGS_PILL_BUTTON_CLASS,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsDivider,
	SettingsSection,
	ToggleRow
} from '@/modules/settings';

interface IntentOption {
	id: string;
	name: string;
}

// ============================================================================
// Intent rows — name + filtering switch; the same row in both lists
// ============================================================================

interface IntentRowsProps {
	intents: IntentOption[];
	filteringOn: boolean;
	enabledSet: Set<string>;
	isPending: boolean;
	onToggle: (intentId: string, next: boolean) => void;
}

function IntentRows({ intents, filteringOn, enabledSet, isPending, onToggle }: IntentRowsProps) {
	return (
		<div className='flex flex-col gap-3'>
			{intents.map((intent, idx) => (
				<div key={intent.id} className='flex flex-col gap-3'>
					{idx > 0 && <SettingsDivider />}
					<div className='flex items-center justify-between gap-3'>
						<span className='text-[13px] leading-[18px] text-ink'>{intent.name}</span>
						<Switch
							checked={enabledSet.has(intent.id)}
							disabled={!filteringOn || isPending}
							aria-label={`Process ${intent.name}`}
							onCheckedChange={(v) => onToggle(intent.id, v)}
						/>
					</div>
				</div>
			))}
		</div>
	);
}

// ============================================================================
// Ingestion › Intents
// ============================================================================

/** Intent-based filtering: the master switch and which intents Kim processes; bypassed ones listed apart. */
export function IntentFilteringContent() {
	const categoriesQuery = usePublishedCSCategories();
	const enabledQuery = useEnabledIntentFilterConfigs();
	const bypassTaggingQuery = useBypassTaggingConfigs();
	const { flags, isLoading: flagsLoading, isError: flagsError, refetch: refetchFlags } = useAccountFlags();
	const upsert = useUpsertIntentFilterConfig();
	const bulkUpdate = useBulkIntentFilterConfig();
	const updateFilteringEnabled = useUpdateIntentFilteringEnabled();

	const [pendingFilteringValue, setPendingFilteringValue] = useState<boolean | null>(null);
	const [search, setSearch] = useState('');

	const filteringOn = !!flags[INTENT_FILTERING_ENABLED_FLAG];
	const enabledSet = useMemo(() => new Set((enabledQuery.data ?? []).map((c) => c.intentId)), [enabledQuery.data]);
	const bypassedSet = useMemo(() => new Set((bypassTaggingQuery.data ?? []).map((c) => c.intentId)), [bypassTaggingQuery.data]);

	const sortedIntents = useMemo<IntentOption[]>(
		() => [...(categoriesQuery.data ?? [])].sort((a, b) => a.id.localeCompare(b.id)).map((c) => ({ id: c.id, name: c.name || c.id })),
		[categoriesQuery.data]
	);

	// Split, not duplicated: bypassed intents live in their own list below, with the same row controls.
	const listedIntents = useMemo(() => {
		const q = search.trim().toLowerCase();
		return sortedIntents.filter(
			(c) => !bypassedSet.has(c.id) && (!q || c.name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q))
		);
	}, [sortedIntents, bypassedSet, search]);

	const bypassedIntents = useMemo(
		() => [...bypassedSet].map((id) => sortedIntents.find((c) => c.id === id) ?? { id, name: id }),
		[bypassedSet, sortedIntents]
	);

	const isPageLoading = categoriesQuery.isLoading || enabledQuery.isLoading || flagsLoading;
	const isPageError = categoriesQuery.isError || enabledQuery.isError || flagsError;

	async function handleRowToggle(intentId: string, nextValue: boolean) {
		try {
			await upsert.mutateAsync({ intentId, isFilteringEnabled: nextValue });
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update intent filtering'));
		}
	}

	async function handleBulkToggle(enabled: boolean) {
		try {
			await bulkUpdate.mutateAsync(enabled);
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update intent filtering'));
		}
	}

	async function handleConfirmFilteringToggle() {
		if (pendingFilteringValue === null) return;
		try {
			await updateFilteringEnabled.mutateAsync(pendingFilteringValue);
			toast.success('Filtering setting updated');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update filtering setting'));
		} finally {
			setPendingFilteringValue(null);
		}
	}

	function handleRetry() {
		void Promise.all([categoriesQuery.refetch(), enabledQuery.refetch(), bypassTaggingQuery.refetch(), refetchFlags()]);
	}

	const rowProps = { filteringOn, enabledSet, isPending: upsert.isPending, onToggle: handleRowToggle };

	return (
		<>
			<SettingsSection label='Intent-based filtering'>
				<SettingsCard>
					<ToggleRow
						title='Only process selected intents'
						description={
							filteringOn
								? 'On. Kim classifies every ticket and responds only for the intents switched on below; all others are left for your team.'
								: 'Off. Kim responds for every intent, whatever the switches below say. Turn on to choose intents one by one.'
						}
						checked={filteringOn}
						disabled={flagsLoading}
						onCheckedChange={setPendingFilteringValue}
					/>
				</SettingsCard>
			</SettingsSection>

			<SettingsSection>
				<div className='flex items-center justify-between'>
					<p className='text-[12px] font-medium leading-4 text-gray-700'>Intents</p>
					<div className='flex items-center gap-2'>
						{[true, false].map((enabled) => (
							<button
								key={String(enabled)}
								type='button'
								onClick={() => handleBulkToggle(enabled)}
								disabled={!filteringOn || bulkUpdate.isPending}
								className={SETTINGS_PILL_BUTTON_CLASS}
							>
								{enabled ? 'Enable all' : 'Disable all'}
							</button>
						))}
					</div>
				</div>
				{isPageLoading ? (
					<SettingsCardSkeleton rows={5} />
				) : isPageError ? (
					<SettingsCard className='items-center py-6'>
						<p className='text-[13px] text-gray-800'>Failed to load filtering settings.</p>
						<Button variant='outline' size='sm' onClick={handleRetry}>
							Retry
						</Button>
					</SettingsCard>
				) : (
					<SettingsCard>
						<SearchInput
							placeholder='Search intents'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className='h-8 w-full border-gray-500 bg-white'
						/>
						{listedIntents.length === 0 ? (
							<p className='py-4 text-center text-[13px] text-gray-800'>
								{search.trim() ? `No intents match "${search}"` : 'No published intents found.'}
							</p>
						) : (
							<IntentRows intents={listedIntents} {...rowProps} />
						)}
					</SettingsCard>
				)}
			</SettingsSection>

			<SettingsSection label='Ticket pre-processing bypass'>
				{isPageLoading || bypassTaggingQuery.isLoading ? (
					<SettingsCardSkeleton rows={2} />
				) : isPageError || bypassTaggingQuery.isError ? (
					<SettingsCard>
						<p className='py-4 text-center text-[13px] text-gray-800'>Failed to load bypassed intents.</p>
					</SettingsCard>
				) : (
					<SettingsCard>
						{bypassedIntents.length === 0 ? (
							<p className='py-4 text-center text-[13px] text-gray-800'>No intents bypass ticket preprocessing.</p>
						) : (
							<IntentRows intents={bypassedIntents} {...rowProps} />
						)}
					</SettingsCard>
				)}
				<p className='text-[12px] leading-4 text-gray-900'>
					Ticket pre-processing is skipped for tickets classified with these intents: no tags are added or removed and the ticket is
					not assigned to Kim, even while pre-processing is on. Choose them on{' '}
					<Link to='/settings/intents?tab=active' className='font-medium text-indigo-500 hover:underline'>
						Intents
					</Link>
					.
				</p>
			</SettingsSection>

			<ConfirmToggleDialog
				pendingValue={pendingFilteringValue}
				icon={pendingFilteringValue === false ? Ban : ListFilter}
				title={pendingFilteringValue === false ? 'Disable Intent-Based Filtering ?' : 'Enable Intent-Based Filtering ?'}
				description={
					pendingFilteringValue === false
						? 'AI responses will be sent for all intents until you turn this back on, regardless of individual intent settings.'
						: 'AI responses will only be sent for intents you have explicitly enabled below.'
				}
				confirmLabel={pendingFilteringValue === false ? 'Disable Filtering' : 'Enable Filtering'}
				isPending={updateFilteringEnabled.isPending}
				onConfirm={() => void handleConfirmFilteringToggle()}
				onCancel={() => setPendingFilteringValue(null)}
			/>
		</>
	);
}
